// ---------------------------------------------------------------------------
// ReviewModal — main tabbed review modal for instruction detail & actions
// ---------------------------------------------------------------------------

import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '@/lib/cn';
import { Button } from '@/components/common';
import { LoadingSpinner } from '@/components/common';
import { RequirePermission } from '@/components/auth';
import { useReviewModalStore } from '@/store/reviewModalStore';
import { useReviewModal } from './useReviewModal';
import { TransactionDetailsTab } from './TransactionDetailsTab';
import { PaymentDetailsTab } from './PaymentDetailsTab';
import { DocumentsTab } from './DocumentsTab';
import { TrackingHistoryTab } from './TrackingHistoryTab';
import { CompliancePanelTab } from './CompliancePanelTab';
import { EventTimeline } from '@/features/audit-trail';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { commentSchema, type CommentFormValues } from './schemas';
import type { ReviewTab } from './types';

// ---------------------------------------------------------------------------
// Tab configuration
// ---------------------------------------------------------------------------

const TABS: { id: ReviewTab; label: string }[] = [
  { id: 'transaction', label: 'Transaction Details' },
  { id: 'payment', label: 'Payment Details' },
  { id: 'documents', label: 'Documents' },
  { id: 'tracking', label: 'Tracking History' },
  { id: 'compliance', label: 'Compliance Panel' },
  { id: 'events', label: 'Events' },
];

// ---------------------------------------------------------------------------
// Action dialog state
// ---------------------------------------------------------------------------

type ActionType = 'approve' | 'hold' | 'decline' | 'cancel' | null;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ReviewModalProps {
  onClose: () => void;
}

export function ReviewModal({ onClose }: ReviewModalProps) {
  const paymentId = useReviewModalStore((s) => s.selectedPaymentId);
  const isOpen = useReviewModalStore((s) => s.isOpen);
  const activeTab = useReviewModalStore((s) => s.activeTab);
  const setActiveTab = useReviewModalStore((s) => s.setActiveTab);
  const closeStore = useReviewModalStore((s) => s.close);

  const {
    payment,
    isLoading,
    approvePayment,
    holdPayment,
    declinePayment,
    cancelPayment,
    addComment,
    uploadDocument,
    isApproving,
    isHolding,
    isDeclining,
    isCancelling,
    isCommenting,
    isUploading,
  } = useReviewModal(paymentId);

  // Action dialog
  const [activeAction, setActiveAction] = useState<ActionType>(null);

  // Comment form
  const {
    register: registerComment,
    handleSubmit: handleCommentSubmit,
    reset: resetComment,
    watch: watchComment,
    formState: { errors: commentErrors },
  } = useForm<CommentFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Zod v3/v4 type mismatch with @hookform/resolvers v4
    resolver: zodResolver(commentSchema as any),
  });

  const commentValue = watchComment('comment') ?? '';

  const handleClose = useCallback(() => {
    closeStore();
    onClose();
  }, [closeStore, onClose]);

  const handleActionConfirm = useCallback(
    (reason?: string) => {
      if (!activeAction) return;

      switch (activeAction) {
        case 'approve':
          // Q2-A: trust the message. The fee was pinned by the webapp at
          // submission; the CBS approve endpoint takes no body.
          approvePayment();
          break;
        case 'hold':
          holdPayment({ reason: reason ?? '' });
          break;
        case 'decline':
          declinePayment({ reason: reason ?? '' });
          break;
        case 'cancel':
          cancelPayment({ reason });
          break;
      }
      setActiveAction(null);
    },
    [activeAction, approvePayment, holdPayment, declinePayment, cancelPayment],
  );

  const onCommentSubmit = useCallback(
    async (values: CommentFormValues) => {
      try {
        await addComment(values);
        resetComment();
      } catch {
        // Error already surfaced via toast in the hook; keep input populated
      }
    },
    [addComment, resetComment],
  );

  // Button visibility per spec

  const isPendingClientApproval = payment?.status === 'pending_client_approval';
  const canApprove = payment?.status === 'pending' || payment?.status === 'held';
  const showApprove = canApprove || isPendingClientApproval;
  const canHold = payment?.status === 'pending' || payment?.status === 'processing';
  const canDecline = payment?.status === 'pending' || payment?.status === 'held';
  const canCancel = payment?.status === 'processing';

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <Dialog.Content
            className={cn(
              'fixed left-1/2 top-1/2 z-50 w-full max-w-3xl -translate-x-1/2 -translate-y-1/2',
              'flex max-h-[90vh] flex-col rounded-xl bg-white shadow-2xl',
              'focus:outline-none',
              'max-sm:max-w-[95vw]',
            )}
            aria-label={`Review ${paymentId ?? 'instruction'}`}
          >
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-[#e8e8e8] px-6 py-4">
              <Dialog.Title className="text-lg font-semibold text-[#333]">
                Review Instruction {payment ? `\u2014 ${payment.payment_id}` : ''}
              </Dialog.Title>
              <Dialog.Close
                className="rounded-md p-1 text-[#999] transition-colors hover:bg-[#f0f0f0] hover:text-[#333] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6B0F1A]"
                aria-label="Close"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M4 4l8 8M12 4l-8 8" />
                </svg>
              </Dialog.Close>
            </div>

            {/* Tab navigation */}
            <div
              className="flex shrink-0 gap-0 overflow-x-auto border-b border-[#e8e8e8] px-6"
              role="tablist"
            >
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  id={`tab-${tab.id}`}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`panel-${tab.id}`}
                  tabIndex={activeTab === tab.id ? 0 : -1}
                  className={cn(
                    'whitespace-nowrap border-b-2 px-4 py-3 text-[13px] font-medium transition-colors',
                    activeTab === tab.id
                      ? 'border-[#6B0F1A] text-[#6B0F1A]'
                      : 'border-transparent text-[#888] hover:text-[#333]',
                  )}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div
              id={`panel-${activeTab}`}
              role="tabpanel"
              aria-labelledby={`tab-${activeTab}`}
              className="flex-1 overflow-y-auto px-6 py-4"
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner text="Loading payment details..." />
                </div>
              ) : payment ? (
                <>
                  {activeTab === 'transaction' && <TransactionDetailsTab payment={payment} />}
                  {activeTab === 'payment' && <PaymentDetailsTab payment={payment} />}
                  {activeTab === 'documents' && (
                    <DocumentsTab
                      documents={payment.documents}
                      onUpload={uploadDocument}
                      isUploading={isUploading}
                    />
                  )}
                  {activeTab === 'tracking' && <TrackingHistoryTab events={payment.events} />}
                  {activeTab === 'compliance' && (
                    <CompliancePanelTab transactionId={payment.transaction_id} />
                  )}
                  {activeTab === 'events' && paymentId !== null && (
                    <EventTimeline kind="payment" parentId={paymentId} />
                  )}
                </>
              ) : (
                <p className="text-sm text-[#999]">Payment not found.</p>
              )}
            </div>

            {/* Comment area (sticky) */}
            {payment && (
              <div className="shrink-0 border-t border-[#e8e8e8] px-6 py-3">
                <form onSubmit={handleCommentSubmit(onCommentSubmit)} className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      {...registerComment('comment')}
                      placeholder="Add a comment..."
                      maxLength={1000}
                      className="w-full rounded-lg border border-[#e0e0e0] px-3 py-2 text-sm outline-none focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#bbb]">
                      {commentValue.length}/1000
                    </span>
                  </div>
                  <Button
                    type="submit"
                    variant="outline"
                    size="sm"
                    disabled={isCommenting || !commentValue.trim()}
                  >
                    {isCommenting ? 'Sending\u2026' : 'Add Comment'}
                  </Button>
                </form>
                {commentErrors.comment && (
                  <p className="mt-1 text-xs text-[#c62828]">{commentErrors.comment.message}</p>
                )}
              </div>
            )}

            {/* Action buttons */}
            {/* Defense-in-depth: route gate (App.tsx /monitoring) already
                requires APPROVE_PAYMENT, but wrapping each action keeps the
                intent grep-able and survives any future route-gate change. */}
            {payment && (
              <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-[#e8e8e8] px-6 py-4">
                <RequirePermission any={['APPROVE_PAYMENT']}>
                  <>
                    {showApprove && (
                      <Button
                        size="sm"
                        className="bg-[#2e7d32] hover:bg-[#1b5e20] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#2e7d32]"
                        disabled={isPendingClientApproval}
                        title={
                          isPendingClientApproval
                            ? 'Waiting for a second webapp user to approve this payment'
                            : undefined
                        }
                        onClick={() => setActiveAction('approve')}
                      >
                        Approve
                      </Button>
                    )}
                    {canHold && (
                      <Button
                        size="sm"
                        className="bg-[#1565c0] hover:bg-[#0d47a1]"
                        onClick={() => setActiveAction('hold')}
                      >
                        Hold
                      </Button>
                    )}
                    {canDecline && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setActiveAction('decline')}
                      >
                        Decline
                      </Button>
                    )}
                    {canCancel && (
                      <Button
                        size="sm"
                        className="bg-[#e65100] hover:bg-[#bf360c]"
                        onClick={() => setActiveAction('cancel')}
                      >
                        Cancel
                      </Button>
                    )}
                  </>
                </RequirePermission>
                <div className="flex-1" />
                <Button variant="ghost" size="sm" onClick={handleClose}>
                  Close
                </Button>
              </div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Confirm dialogs */}
      <ConfirmActionDialog
        isOpen={activeAction === 'approve'}
        title="Approve Payment"
        message="Are you sure you want to approve this payment?"
        confirmText="Approve"
        onConfirm={handleActionConfirm}
        onCancel={() => setActiveAction(null)}
        isLoading={isApproving}
      />

      <ConfirmActionDialog
        isOpen={activeAction === 'hold'}
        title="Place on Hold"
        message="Are you sure you want to place this payment on hold?"
        confirmText="Hold"
        onConfirm={handleActionConfirm}
        onCancel={() => setActiveAction(null)}
        isLoading={isHolding}
        showReason
        reasonRequired
        reasonPlaceholder="Enter reason for placing on hold..."
      />

      <ConfirmActionDialog
        isOpen={activeAction === 'decline'}
        title="Decline Payment"
        message="Are you sure you want to decline this payment?"
        confirmText="Decline"
        onConfirm={handleActionConfirm}
        onCancel={() => setActiveAction(null)}
        isDangerous
        isLoading={isDeclining}
        showReason
        reasonRequired
        reasonPlaceholder="Enter reason for declining..."
      />

      <ConfirmActionDialog
        isOpen={activeAction === 'cancel'}
        title="Cancel Payment"
        message="Cancel this payment submission with Convera?"
        confirmText="Cancel Payment"
        cancelText="Go Back"
        onConfirm={handleActionConfirm}
        onCancel={() => setActiveAction(null)}
        isDangerous
        isLoading={isCancelling}
        showReason
        reasonPlaceholder="Enter reason for cancellation (optional)..."
      />
    </>
  );
}
